import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAgreementStore } from '@/modules/agreements/store/agreementStore';
import { Skeleton } from '@/components/ui/skeleton';
import { usePunchcardCollects } from '../hooks/usePunchcardCollects';
import { useCollectsByDate } from '../hooks/useCollectsByDate';
import { useAgreementInfo } from '../hooks/useAgreementInfo';
import { CollectsCalendar } from '../components/CollectsCalendar';
import { DayTimelineCard } from '../components/DayTimelineCard';
import { CollectDetailDialog } from '../components/CollectDetailDialog';
import type { Collect } from '../types';

function getDayLabel(date: string): string {
  return format(parseISO(`${date}T12:00:00`), 'EEE', { locale: ptBR })
    .replace('.', '')
    .toUpperCase()
    .substring(0, 3);
}

function getDateLabel(date: string): string {
  return format(parseISO(`${date}T12:00:00`), 'dd/MM');
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function calcDayTotalLabel(collects: Collect[]): string | undefined {
  if (collects.length === 0 || collects.length % 2 !== 0) return undefined;
  const sorted = [...collects].sort((a, b) => a.time.localeCompare(b.time));
  let total = 0;
  for (let i = 0; i + 1 < sorted.length; i += 2) {
    const e = sorted[i];
    const x = sorted[i + 1];
    if (e && x) {
      const diff = timeToMinutes(x.time) - timeToMinutes(e.time);
      if (diff > 0) total += diff;
    }
  }
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}min`;
}

export default function PunchcardCalendarPage() {
  const navigate = useNavigate();
  const { activeAgreement } = useAgreementStore();
  const { data: collects = [], isLoading } = usePunchcardCollects(activeAgreement?.uuid);
  const { data: agreementInfo = null } = useAgreementInfo(activeAgreement?.uuid);
  const today = format(new Date(), 'yyyy-MM-dd');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [selectedDate, setSelectedDate] = useState<string | null>(today);
  const [selectedCollect, setSelectedCollect] = useState<Collect | null>(null);

  const collectsByDate = useCollectsByDate(collects);

  const markedDates = useMemo(
    () => new Set(Object.keys(collectsByDate)),
    [collectsByDate],
  );

  const dayCollects = selectedDate ? (collectsByDate[selectedDate] ?? []) : [];

  if (isLoading) {
    return (
      <div role="status" aria-label="Carregando calendário" className="space-y-3 px-4 py-4">
        <Skeleton className="h-64 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-6 pb-4 pt-6">
        <h1 className="text-base font-semibold text-slate-900">Espelho de Ponto</h1>
        <button
          onClick={() => navigate('/punchcard/history')}
          className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-600 hover:bg-slate-100 transition-colors"
          aria-label="Ver lista de histórico"
        >
          <List className="h-4 w-4" aria-hidden="true" />
        </button>
      </header>

      <div className="space-y-3 px-4 py-4">
        <CollectsCalendar
          month={selectedMonth}
          onMonthChange={(m) => {
            setSelectedMonth(m);
            setSelectedDate(null);
          }}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
          markedDates={markedDates}
        />

        {selectedDate && (
          <div>
            {dayCollects.length === 0 ? (
              <p className="py-4 text-center text-sm text-slate-400">
                Nenhuma marcação em{' '}
                {format(parseISO(`${selectedDate}T12:00:00`), "dd 'de' MMMM", { locale: ptBR })}.
              </p>
            ) : (
              <DayTimelineCard
                date={selectedDate}
                dayLabel={getDayLabel(selectedDate)}
                dateLabel={getDateLabel(selectedDate)}
                collects={dayCollects}
                isToday={selectedDate === today}
                totalLabel={calcDayTotalLabel(dayCollects)}
                onCollectClick={setSelectedCollect}
                onCollectPeekEnd={() => setSelectedCollect(null)}
              />
            )}
          </div>
        )}
      </div>

      <CollectDetailDialog
        collect={selectedCollect}
        agreementInfo={agreementInfo}
        agreementUuid={activeAgreement?.uuid}
        open={selectedCollect !== null}
        onOpenChange={(open) => { if (!open) setSelectedCollect(null); }}
      />
    </div>
  );
}
